import { useState } from "react";
import { Link } from "wouter";
import { Check, Copy, Image as ImageIcon, MessageCircle, Pencil, Trash2, Truck, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageSkeleton } from "@/components/data/page-skeleton";
import { StatusChip, ChipPagamento } from "@/components/mercado/status-chip";
import { FormConferencia } from "@/components/mercado/form-conferencia";
import { DialogPagamentoLote } from "@/components/mercado/dialog-pagamento-lote";
import { usePode } from "@/lib/permissoes";
import { origemAtual, useNavegacao } from "@/lib/navegacao";
import { useToast } from "@/hooks/use-toast";
import { useConferirComprador } from "@/hooks/use-conferencia";
import {
  useCargasEmFormacao, useDocumentoDoRomaneio, useExcluirRomaneio,
  useIncluirEmCarga, useRomaneio, useTirarDaCarga,
} from "@/hooks/use-romaneios";
import { fetchJson, apiRequest } from "@/lib/queryClient";
import { useQuery } from "@tanstack/react-query";
import { fmtDataBR, fmtKgBR, fmtMoedaBR, fmtDiaCurto } from "@/lib/formato-mercado";
import { lerRef, type DetalheRomaneioDTO } from "@shared/mercado-romaneios";
import { linkWhatsAppCom } from "@shared/mercado";
import { nomeCurtoDoProdutor, nomeDoProdutor } from "@shared/mercado-inicio";

/**
 * O ROMANEIO em painel lateral — não em outra página.
 *
 * ⚠️ A LISTA CONTINUA ATRÁS, e a decisão é de fluxo: ele confere vários
 * seguidos, e cada volta a uma página inteira custaria recarregar os quatro
 * tiles e reencontrar a linha onde estava. No celular o painel ocupa a tela
 * toda, porque ali não há "atrás".
 *
 * ⚠️ A URL É A MESMA COISA para os dois: `/mercado/romaneios/:ref` monta a lista
 * com o painel por cima. Voltar fecha o painel e devolve a lista COM OS FILTROS,
 * porque eles estão na query string da entrada anterior do histórico.
 */

export function PainelRomaneio({ refRomaneio }: { refRomaneio: string }) {
  const { voltar } = useNavegacao();
  const consulta = useRomaneio(refRomaneio);
  const { data, isLoading, isError } = consulta;

  function fechar() {
    voltar("/mercado/romaneios");
  }

  return (
    <Sheet open onOpenChange={o => { if (!o) fechar(); }}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-xl p-0 flex flex-col gap-0"
        // O cabeçalho do painel já tem o "Fechar" dele (e o do erro, "Voltar").
        fecharEmbutido={false}
        data-testid="painel-romaneio"
      >
        {isLoading ? (
          <div className="p-5"><PageSkeleton variante="cards" linhas={3} /></div>
        ) : isError || !data ? (
          // Romaneio de outro corretor chega aqui como 404 — igual a inexistente,
          // de propósito. Nunca esqueleto eterno: a mensagem encerra a espera.
          <div className="p-6 text-center space-y-3">
            <p className="font-bold">Romaneio não encontrado.</p>
            <Button variant="outline" onClick={fechar}>Voltar</Button>
          </div>
        ) : (
          <Conteudo dados={data} refRomaneio={refRomaneio} onFechar={fechar} />
        )}
      </SheetContent>
    </Sheet>
  );
}

function Conteudo({
  dados, refRomaneio, onFechar,
}: { dados: DetalheRomaneioDTO; refRomaneio: string; onFechar: () => void }) {
  const podeOperar = usePode("mercado.corretor.operar");
  const doRizoma = dados.origem === "RIZOMA";

  return (
    <>
      <header className="border-b p-5 pb-4 flex items-start justify-between gap-3 shrink-0">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            {doRizoma
              ? "Recebido pelo Rizoma"
              : `Registro próprio${dados.numero !== "registro" ? ` · bloco nº ${dados.numero}` : ""}`}
          </p>
          <h2 className="text-lg font-bold tracking-tight truncate mt-0.5" data-testid="titulo-romaneio">
            {doRizoma ? `Romaneio ${dados.numero}` : `Romaneio · ${dados.produtor.nome}`}
          </h2>
          <p className="text-[13px] text-muted-foreground mt-0.5 truncate" data-testid="subtitulo-romaneio">
            {/* "Hugo (Fazenda Boa Vista)" — a pessoa e a fazenda (Hugo, 24/09).
                No registro próprio o segundo campo é a cidade: vai separado. */}
            {doRizoma
              ? nomeDoProdutor(dados.produtor.nome, dados.produtor.fazenda)
              : <>{dados.produtor.nome}{dados.produtor.fazenda && ` · ${dados.produtor.fazenda}`}</>}
            {" · carregado "}{fmtDiaCurto(dados.data)}
          </p>
        </div>
        <Button variant="ghost" size="icon" onClick={onFechar} aria-label="Fechar" data-testid="button-fechar-painel">
          <X className="h-4 w-4" />
        </Button>
      </header>

      <div className="flex-1 overflow-y-auto p-5 space-y-4">
        <TresNumeros dados={dados} />

        <Produtor dados={dados} />

        {dados.acoes.conferir && podeOperar && <CaixaConferencia dados={dados} refRomaneio={refRomaneio} />}

        <Itens dados={dados} />

        <Situacao dados={dados} refRomaneio={refRomaneio} podeOperar={podeOperar} />

        <Historico dados={dados} />
      </div>

      <Rodape dados={dados} refRomaneio={refRomaneio} podeOperar={podeOperar} onFechar={onFechar} />
    </>
  );
}

// ─── Os três números ─────────────────────────────────────────────────────────

function TresNumeros({ dados }: { dados: DetalheRomaneioDTO }) {
  return (
    <div className="grid grid-cols-3 gap-2 rounded-lg border bg-card p-3" data-testid="numeros-romaneio">
      <Numero rotulo="kg" valor={fmtKgBR(dados.kgBase)} />
      <Numero rotulo="Valor" valor={fmtMoedaBR(dados.valor)} />
      <Numero
        rotulo="Vencimento"
        // "—" e não uma data inventada: sem prazo combinado, não há prazo.
        valor={dados.vencimento ? fmtDataBR(dados.vencimento) : "—"}
      />
    </div>
  );
}

function Numero({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{rotulo}</p>
      <p className="font-mono font-semibold tabular-nums truncate">{valor}</p>
    </div>
  );
}

// ─── Conferência (C3) ────────────────────────────────────────────────────────

/**
 * "Confira este romaneio" — a caixa âmbar.
 *
 * ⚠️ DOIS BOTÕES, não um formulário sempre aberto: o caso comum é "bateu", e ele
 * tem de custar um toque no celular, no pátio, com a carga descendo do caminhão.
 * "Informar divergência" abre o formulário item a item, que é o mesmo do link
 * público — duas versões divergiriam na primeira regra nova.
 *
 * Desde 24/09 confere-se kg, PREÇO POR KG e VENCIMENTO (o produtor pode emitir
 * um valor que não é o combinado). O diálogo abre JÁ no formulário: quem tocou
 * em "Informar divergência" já disse que não bate.
 */
function CaixaConferencia({ dados, refRomaneio }: { dados: DetalheRomaneioDTO; refRomaneio: string }) {
  const { toast } = useToast();
  const [divergindo, setDivergindo] = useState(false);
  const lido = lerRef(refRomaneio);
  const conferir = useConferirComprador(lido?.id);
  const documento = useDocumentoDoRomaneio(refRomaneio, divergindo);

  function confirmar() {
    conferir.mutate({ bate: true }, {
      onSuccess: () => toast({ title: "Romaneio conferido" }),
      // Nunca engolir: sem isto ele toca em Confirmar e nada acontece.
      onError: (e: Error) => toast({
        title: "Não consegui registrar a conferência",
        description: e.message,
        variant: "destructive",
      }),
    });
  }

  return (
    <>
      <section
        className="rounded-lg border border-[hsl(var(--warning,38_92%_50%))] bg-[hsl(var(--warning,38_92%_50%)/0.08)] p-4 space-y-3"
        data-testid="caixa-conferencia"
      >
        <div>
          <p className="font-semibold text-sm">Confira este romaneio</p>
          <p className="text-[13px] text-muted-foreground mt-0.5" data-testid="texto-caixa-conferencia">
            {nomeCurtoDoProdutor(dados.produtor.nome, dados.produtor.fazenda)} enviou {fmtKgBR(dados.kgEmitido)} por {fmtMoedaBR(dados.valor)}
            {dados.vencimento ? <>, vencimento {fmtDataBR(dados.vencimento)}</> : ", sem vencimento"}.
            {" "}Se bateu com o combinado, confirme. Se veio diferente — kg, preço ou vencimento —, informe:
            o que você registrar passa a ser a base do valor.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" onClick={() => setDivergindo(true)} data-testid="button-informar-divergencia">
            Informar divergência
          </Button>
          <Button size="sm" onClick={confirmar} disabled={conferir.isPending} data-testid="button-confirmar-kg">
            <Check className="h-4 w-4 mr-1.5" />{conferir.isPending ? "Confirmando…" : "Está certo"}
          </Button>
        </div>
      </section>

      <Dialog open={divergindo} onOpenChange={o => !o && setDivergindo(false)}>
        {/* `max-h` + rolagem: com kg, preço e vencimento o formulário passa da
            altura de um celular com o teclado aberto. */}
        <DialogContent className="sm:max-w-lg max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Informar divergência</DialogTitle>
            <DialogDescription>
              Ajuste o que veio diferente do combinado. O valor é recalculado pelo servidor.
            </DialogDescription>
          </DialogHeader>
          {documento.isLoading ? (
            <PageSkeleton variante="cards" linhas={2} />
          ) : documento.isError || !documento.data ? (
            <p className="text-sm text-destructive">Não consegui carregar o romaneio do produtor.</p>
          ) : (
            <FormConferencia
              itens={documento.data.itens}
              conferencia={documento.data.conferencia}
              salvando={conferir.isPending}
              erro={conferir.error?.message ?? null}
              iniciarEditando
              onCancelar={() => setDivergindo(false)}
              comVencimento={{ atual: dados.vencimento ?? null, dataVenda: dados.data }}
              onConferir={(d) => conferir.mutate(d, {
                onSuccess: () => { setDivergindo(false); toast({ title: "Conferência registrada" }); },
                onError: (e: Error) => toast({
                  title: "Não consegui registrar a conferência",
                  description: e.message,
                  variant: "destructive",
                }),
              })}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

// ─── Itens ───────────────────────────────────────────────────────────────────

/**
 * QUEM ENTREGOU — o bloco que faltava.
 *
 * ⚠️ NOME E FAZENDA SÃO CAMPOS DIFERENTES desde o #240, e é por isso que este
 * bloco existe em vez de uma linha só no cabeçalho: `nome` é a PESSOA (como o
 * corretor a conhece na carteira, ou quem formalizou do lado do produtor) e
 * `fazenda` é a ORGANIZAÇÃO. Quando os dois são iguais — o caso comum, porque o
 * corretor salva o contato com o nome da fazenda — `fazenda` vem nula, e mostrar
 * "Fazenda Boa Vista · Fazenda Boa Vista" seria a tela se repetindo.
 *
 * A CIDADE é a praça de onde a fruta saiu, e é o que distingue dois produtores
 * de mesmo nome na hora de conferir um carregamento.
 */
function Produtor({ dados }: { dados: DetalheRomaneioDTO }) {
  const { nome, fazenda, noRizoma, telefone } = dados.produtor;

  return (
    <section className="rounded-lg border bg-card" data-testid="produtor-romaneio">
      <header className="px-4 py-2.5 border-b flex items-baseline justify-between gap-2">
        <h3 className="font-semibold text-sm">Produtor</h3>
        {noRizoma
          ? <StatusChip kind="conf">No Rizoma</StatusChip>
          : <span className="text-xs text-muted-foreground">sem conta no Rizoma</span>}
      </header>
      <div className="p-4 space-y-2.5 text-[13px]">
        <Linha rotulo="Nome"><b className="text-foreground">{nome}</b></Linha>
        {fazenda && <Linha rotulo="Fazenda"><span className="text-foreground">{fazenda}</span></Linha>}
        {dados.produtorCidade && (
          <Linha rotulo="Cidade"><span className="text-foreground">{dados.produtorCidade}</span></Linha>
        )}
        {telefone && (
          <Linha rotulo="WhatsApp">
            {/* `wa.me` e não um `tel:`: o combinado da praça acontece no WhatsApp,
                e abrir o discador seria mandar o corretor para o lugar errado. */}
            <a
              // Texto vazio: aqui o link só ABRE a conversa. O normalizador do
              // número (o 55 que entra uma vez só) é o que se reaproveita.
              href={linkWhatsAppCom(telefone, "")}
              target="_blank" rel="noopener noreferrer"
              className="text-foreground underline underline-offset-2"
              data-testid="link-whatsapp-produtor"
            >
              {telefone}
            </a>
          </Linha>
        )}
      </div>
    </section>
  );
}

/** "82,8%" — uma casa, como no documento do produtor. */
function fmtPct(pct: number): string {
  return `${pct.toFixed(1).replace(".", ",")}%`;
}

function Itens({ dados }: { dados: DetalheRomaneioDTO }) {
  const totalKg = dados.itens.reduce((a, i) => a + i.kg, 0);
  const totalValor = dados.itens.reduce((a, i) => a + i.valor, 0);

  return (
    <section className="rounded-lg border bg-card overflow-hidden" data-testid="itens-romaneio">
      <header className="px-4 py-2.5 border-b flex items-baseline justify-between gap-2">
        <h3 className="font-semibold text-sm">Itens</h3>
        <span className="text-xs text-muted-foreground">
          {dados.origem === "RIZOMA" ? "como emitido pelo produtor" : "como você registrou"}
        </span>
      </header>
      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-muted-foreground">
          <tr>
            <th className="text-left font-medium px-4 py-2">Classificação</th>
            <th className="text-right font-medium px-2 py-2">kg</th>
            <th className="text-right font-medium px-2 py-2">R$/kg</th>
            <th className="text-right font-medium px-4 py-2">Valor</th>
          </tr>
        </thead>
        <tbody>
          {dados.itens.map((i, idx) => (
            <tr key={`${i.classificacao}-${idx}`} className="border-t">
              <td className="px-4 py-2">
                <span
                  className="inline-block h-2 w-2 rounded-sm mr-2 align-middle"
                  style={{ background: `hsl(var(--chart-${(idx % 5) + 1}))` }}
                  aria-hidden
                />
                {i.classificacao}
              </td>
              <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtKgBR(i.kg)}</td>
              <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtMoedaBR(i.precoKg)}</td>
              <td className="px-4 py-2 text-right font-mono tabular-nums">{fmtMoedaBR(i.valor)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-muted/30 text-sm font-semibold">
          <tr className="border-t">
            <td className="px-4 py-2">Total</td>
            <td className="px-2 py-2 text-right font-mono tabular-nums">{fmtKgBR(totalKg)}</td>
            <td />
            <td className="px-4 py-2 text-right font-mono tabular-nums">{fmtMoedaBR(totalValor)}</td>
          </tr>
        </tfoot>
      </table>

      {/* A DISTRIBUIÇÃO — a mesma linha do documento do produtor, pela mesma
          função (`distribuicaoPorVariedade`). Relativa ao total da VARIEDADE, e
          não do romaneio: é assim que o corretor lê a qualidade de um lote. Vem
          vazia quando cada variedade tem uma classificação só, e aí não aparece. */}
      {dados.distribuicao.length > 0 && (
        <p className="px-4 py-2.5 border-t text-[13px] text-muted-foreground" data-testid="distribuicao-romaneio">
          Distribuição:{" "}
          {dados.distribuicao.map(d => `${d.rotulo} ${fmtPct(d.pct)}`).join(" · ")}
        </p>
      )}

      {/* O ajuste da conferência vira frase: a tabela mostra o que o produtor
          emitiu, e esta linha diz o que passou a valer e por quê. */}
      {dados.ajusteKg != null && (
        <p className="px-4 py-2.5 border-t text-[13px] text-muted-foreground">
          Conferido por você: <b className="font-mono text-foreground">{fmtKgBR(dados.kgBase)}</b>
          {" "}({dados.ajusteKg > 0 ? "+" : "−"}{fmtKgBR(Math.abs(dados.ajusteKg))}).
          O valor a pagar considera os kg conferidos.
        </p>
      )}
      {dados.conferenciaObs && (
        <p className="px-4 py-2.5 border-t text-[13px] text-muted-foreground">
          Observação da conferência: {dados.conferenciaObs}
        </p>
      )}
    </section>
  );
}

// ─── Situação ────────────────────────────────────────────────────────────────

function Situacao({
  dados, refRomaneio, podeOperar,
}: { dados: DetalheRomaneioDTO; refRomaneio: string; podeOperar: boolean }) {
  const [incluindo, setIncluindo] = useState(false);
  const [informando, setInformando] = useState(false);

  return (
    <section className="rounded-lg border bg-card" data-testid="situacao-romaneio">
      <header className="px-4 py-2.5 border-b"><h3 className="font-semibold text-sm">Situação</h3></header>
      <div className="p-4 space-y-2.5 text-[13px]">
        <Linha rotulo="Conferência">
          {dados.conferencia == null
            ? <span className="text-muted-foreground">não se aplica</span>
            : dados.conferencia === "A_CONFERIR" ? <StatusChip kind="warn">A conferir</StatusChip>
              : <StatusChip kind="conf">{dados.conferencia === "AJUSTADO" ? "Ajustado" : "Conferido"}</StatusChip>}
        </Linha>
        <Linha rotulo="Carga">
          {dados.carga ? (
            <span className="flex items-center gap-2">
              <Link href={`/mercado/cargas/${dados.carga.id}`} state={origemAtual()}>
                <StatusChip kind={dados.carga.status === "ENTREGUE" ? "pago" : dados.carga.status === "FECHADA" ? "vista" : "warn"}>
                  {dados.carga.identificador}
                </StatusChip>
              </Link>
              {podeOperar && dados.carga.status === "EM_FORMACAO" && <BotaoTirarDaCarga refRomaneio={refRomaneio} />}
            </span>
          ) : (
            <span className="flex items-center gap-2">
              <span className="inline-flex items-center rounded-full border border-dashed px-2 py-0.5 text-[11px] text-muted-foreground">
                Sem carga
              </span>
              {podeOperar && (
                <Button variant="ghost" size="sm" className="h-7" onClick={() => setIncluindo(true)} data-testid="button-incluir-em-carga">
                  Incluir em carga
                </Button>
              )}
            </span>
          )}
        </Linha>
        <Linha rotulo="Pagamento">
          <span className="flex items-center gap-2">
            <ChipPagamento status={dados.pagamento} />
            {/* "Registrar pagamento" nos dois casos (romaneio do Rizoma e registro
                próprio): QUEM REGISTRA QUITA, e é o mesmo ato e o mesmo diálogo do
                A pagar — um lugar para o valor, a data e a forma. */}
            {podeOperar && dados.dinheiro.saldo > 0 && (
              <Button
                variant="ghost" size="sm" className="h-7"
                onClick={() => setInformando(true)}
                data-testid="button-informar-pagamento"
              >
                Registrar pagamento
              </Button>
            )}
          </span>
        </Linha>

        {/* ⚠️ O SALDO POR EXTENSO, e não só o chip. "A pagar" não diz QUANTO, e
            sem o número o corretor abriria `/mercado/pagar` para descobrir — que
            é a ida e volta que este painel existe para poupar.

            "Informado, aguardando o produtor" SAIU: com "quem registra quita" o
            que foi registrado já é pago, e a linha só poderia mostrar zero. */}
        <div className="pt-1 border-t space-y-2.5">
          {dados.dinheiro.pago > 0 && (
            <Linha rotulo="Pago">
              <b className="font-mono tabular-nums text-foreground">{fmtMoedaBR(dados.dinheiro.pago)}</b>
            </Linha>
          )}
          <Linha rotulo="Em aberto">
            <b className="font-mono tabular-nums text-foreground" data-testid="saldo-romaneio">
              {fmtMoedaBR(dados.dinheiro.saldo)}
            </b>
          </Linha>
        </div>
      </div>

      <DialogIncluirEmCarga aberta={incluindo} onFechar={() => setIncluindo(false)} refRomaneio={refRomaneio} />
      <DialogPagamentoLote
        linhas={informando ? [{
          natureza: dados.origem === "RIZOMA" ? "ROMANEIO" : "REGISTRO",
          // O id do ALVO é o do documento, sem o prefixo da rota: o `ref` é
          // endereço de tela, e o POST espera o id do compartilhamento ou do item.
          alvoId: dados.ref.substring(dados.ref.indexOf(":") + 1),
          // "Hugo (Fazenda Boa Vista)" — a PESSOA com a fazenda entre parênteses,
          // pelo mesmo `nomeDoProdutor` do painel do Início. Quem paga precisa
          // saber com quem está acertando.
          produtorNome: nomeDoProdutor(dados.produtor.nome, dados.produtor.fazenda),
          documento: dados.numero,
          saldo: dados.dinheiro.saldo,
        }] : null}
        onFechar={() => setInformando(false)}
      />
    </section>
  );
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-2">
      <span className="text-muted-foreground">{rotulo}</span>
      {children}
    </div>
  );
}

function BotaoTirarDaCarga({ refRomaneio }: { refRomaneio: string }) {
  const { toast } = useToast();
  const tirar = useTirarDaCarga();
  return (
    <Button
      variant="ghost"
      size="sm"
      className="h-7"
      disabled={tirar.isPending}
      onClick={() => tirar.mutate(refRomaneio, {
        // O romaneio NÃO é apagado (073): volta para "Sem carga" e segue em
        // "A pagar". O texto diz isso, porque antes ele sumia.
        onSuccess: () => toast({ title: "Romaneio tirado da carga", description: "Ele continua na sua lista, sem carga." }),
        onError: (e: Error) => toast({ title: "Não consegui tirar da carga", description: e.message, variant: "destructive" }),
      })}
      data-testid="button-tirar-da-carga"
    >
      Tirar da carga
    </Button>
  );
}

function DialogIncluirEmCarga({
  aberta, onFechar, refRomaneio,
}: { aberta: boolean; onFechar: () => void; refRomaneio: string }) {
  const { toast } = useToast();
  const { data: cargas } = useCargasEmFormacao(aberta);
  const incluir = useIncluirEmCarga();
  const [cargaId, setCargaId] = useState("");

  const emFormacao = cargas ?? [];

  return (
    <Dialog open={aberta} onOpenChange={o => !o && onFechar()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Incluir em carga</DialogTitle></DialogHeader>
        {emFormacao.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nenhuma carga em formação. Crie uma em Cargas e volte aqui — ou selecione vários romaneios na
            lista e monte a carga já com eles.
          </p>
        ) : (
          <Select value={cargaId} onValueChange={setCargaId}>
            <SelectTrigger data-testid="select-carga"><SelectValue placeholder="Escolha a carga" /></SelectTrigger>
            <SelectContent>
              {emFormacao.map(c => (
                <SelectItem key={c.id} value={c.id}>
                  {c.identificador}
                  {c.faltam != null && c.faltam > 0 && ` · faltam ${fmtKgBR(c.faltam)}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onFechar}>Cancelar</Button>
          <Button
            disabled={!cargaId || incluir.isPending}
            onClick={() => incluir.mutate({ refs: [refRomaneio], cargaId }, {
              onSuccess: (r) => { onFechar(); toast({ title: `Incluído na ${r.identificador}` }); },
              onError: (e: Error) => toast({ title: "Não consegui incluir", description: e.message, variant: "destructive" }),
            })}
            data-testid="button-confirmar-incluir"
          >
            {incluir.isPending ? "Incluindo…" : "Incluir"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Histórico ───────────────────────────────────────────────────────────────

function Historico({ dados }: { dados: DetalheRomaneioDTO }) {
  if (dados.historico.length === 0) return null;
  return (
    <section className="rounded-lg border bg-card" data-testid="historico-romaneio">
      <header className="px-4 py-2.5 border-b"><h3 className="font-semibold text-sm">Histórico</h3></header>
      <ul className="p-4 space-y-2 text-[13px]">
        {dados.historico.map((e, i) => (
          <li key={`${e.tipo}-${e.em}-${i}`} className="flex items-baseline justify-between gap-3">
            {/* O texto vem PRONTO do servidor: a tela não traduz código de
                evento, porque o mesmo tipo conta histórias diferentes conforme o
                payload. */}
            <span className="min-w-0">{e.texto}</span>
            <span className="shrink-0 text-xs text-muted-foreground tabular-nums">{fmtDataHora(e.em)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function fmtDataHora(iso: string): string {
  const d = new Date(iso);
  return `${d.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} ${d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`;
}

// ─── Rodapé ──────────────────────────────────────────────────────────────────

function Rodape({
  dados, refRomaneio, podeOperar, onFechar,
}: { dados: DetalheRomaneioDTO; refRomaneio: string; podeOperar: boolean; onFechar: () => void }) {
  const doRizoma = dados.origem === "RIZOMA";
  const primeiroNome = dados.produtor.nome.trim().split(/\s+/)[0];

  return (
    <footer className="border-t p-4 flex flex-wrap gap-2 shrink-0" data-testid="rodape-romaneio">
      {doRizoma
        ? (
          <>
            <BotaoDocumento refRomaneio={refRomaneio} />
            <BotaoImagem refRomaneio={refRomaneio} />
            <BotaoTextoDoRomaneio refRomaneio={refRomaneio} />
          </>
        )
        : podeOperar && (
          <>
            <Button variant="outline" size="sm" asChild data-testid="button-editar-romaneio">
              <Link href={`/mercado/romaneios/${encodeURIComponent(refRomaneio)}/editar`} state={origemAtual()}>
                <Pencil className="h-4 w-4 mr-1.5" />Editar
              </Link>
            </Button>
            <BotaoEnviar refRomaneio={refRomaneio} primeiroNome={primeiroNome} />
          </>
        )}

      <div className="ml-auto flex gap-2">
        {podeOperar && dados.acoes.excluir && (
          <BotaoExcluir refRomaneio={refRomaneio} onExcluido={onFechar} />
        )}
        {dados.carga && (
          <Button size="sm" asChild data-testid="button-abrir-carga">
            <Link href={`/mercado/cargas/${dados.carga.id}`} state={origemAtual()}>
              <Truck className="h-4 w-4 mr-1.5" />Abrir {dados.carga.identificador}
            </Link>
          </Button>
        )}
      </div>
    </footer>
  );
}

/**
 * A FOTO do romaneio — a mesma que o produtor gera da nota dele.
 *
 * ⚠️ `window.open` e não `fetch` + blob: o endpoint responde com
 * `Content-Disposition: attachment`, e deixar o navegador cuidar do download é o
 * que faz a foto cair na galeria do celular — que é de onde ela vai para o
 * WhatsApp. Um blob em memória exigiria um `<a download>` sintético e no iOS
 * abriria uma aba branca.
 *
 * Só no romaneio do Rizoma: o registro próprio é a nota que o corretor mesmo
 * digitou, e o documento dela é o `/p/:token` que o botão de enviar já usa.
 */
function BotaoImagem({ refRomaneio }: { refRomaneio: string }) {
  return (
    <Button
      variant="outline"
      size="sm"
      onClick={() => window.open(
        `/api/mercado/comprador/romaneios/${encodeURIComponent(refRomaneio)}/imagem`,
        "_blank",
        "noopener",
      )}
      data-testid="button-imagem-romaneio"
    >
      <ImageIcon className="h-4 w-4 mr-1.5" />Imagem
    </Button>
  );
}

/**
 * O TEXTO que vai junto com a foto no WhatsApp.
 *
 * ⚠️ COPIAR, e não abrir o WhatsApp. O destinatário aqui não é sabido — o
 * corretor repassa o romaneio para quem ele quiser, e o único telefone que a
 * tela conhece é o dele mesmo. Copiar deixa a escolha com ele: cola na conversa
 * que quiser, junto da imagem que acabou de baixar.
 *
 * ⚠️ O QUE ESTE TEXTO CARREGA é a chave PIX do produtor. Ninguém copia uma chave
 * de dentro de uma foto, e era isso que faltava para o romaneio ser pagável sem
 * sair do app.
 */
function BotaoTextoDoRomaneio({ refRomaneio }: { refRomaneio: string }) {
  const { toast } = useToast();
  const [copiando, setCopiando] = useState(false);

  async function copiar() {
    setCopiando(true);
    try {
      const r = await fetchJson<{ texto: string }>(
        `/api/mercado/comprador/romaneios/${encodeURIComponent(refRomaneio)}/texto`,
      );
      await navigator.clipboard.writeText(r.texto);
      toast({ title: "Texto copiado", description: "Cole no WhatsApp junto com a imagem." });
    } catch (e) {
      // Nunca engolir: sem isto ele toca, nada acontece, e ele cola o texto
      // anterior sem perceber.
      toast({
        title: "Não consegui copiar",
        description: e instanceof Error ? e.message : "Tente de novo.",
        variant: "destructive",
      });
    } finally {
      setCopiando(false);
    }
  }

  return (
    <Button variant="outline" size="sm" onClick={copiar} disabled={copiando} data-testid="button-texto-romaneio">
      <Copy className="h-4 w-4 mr-1.5" />{copiando ? "Copiando…" : "Copiar texto"}
    </Button>
  );
}

/**
 * "Ver documento do produtor" — a nota como ELE a emitiu.
 *
 * ⚠️ Num diálogo e não em outra rota: a tabela do painel já mostra os itens como
 * o romaneio os traz, e o que este botão acrescenta é o cabeçalho da NOTA
 * (número, emissão, totais do documento). Uma página a mais custaria sair do
 * painel e perder o lugar na lista para ler três linhas.
 */
function BotaoDocumento({ refRomaneio }: { refRomaneio: string }) {
  const [aberto, setAberto] = useState(false);
  const documento = useDocumentoDoRomaneio(refRomaneio, aberto);

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => setAberto(true)} data-testid="button-ver-documento">
        Ver documento do produtor
      </Button>
      <Dialog open={aberto} onOpenChange={o => !o && setAberto(false)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Documento do produtor</DialogTitle>
            <DialogDescription>A nota como o produtor a emitiu no Rizoma.</DialogDescription>
          </DialogHeader>
          {documento.isLoading ? (
            <PageSkeleton variante="cards" linhas={2} />
          ) : documento.isError || !documento.data ? (
            <p className="text-sm text-destructive">Não consegui carregar o documento.</p>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-2">
                <Numero rotulo="Romaneio" valor={documento.data.numero} />
                <Numero rotulo="Emissão" valor={fmtDataBR(documento.data.dataEmissao)} />
                <Numero rotulo="Total kg" valor={fmtKgBR(documento.data.totalKg)} />
                <Numero rotulo="Total" valor={fmtMoedaBR(documento.data.totalValor)} />
              </div>
              <ul className="rounded-lg border divide-y">
                {documento.data.itens.map((i, idx) => (
                  <li key={`${i.variedade}-${i.qualidade}-${idx}`} className="flex justify-between gap-2 px-3 py-2">
                    <span>{i.variedade}{i.qualidade ? ` ${i.qualidade}` : ""}</span>
                    <span className="font-mono tabular-nums">
                      {fmtKgBR(i.kg)} × {fmtMoedaBR(i.precoKg)}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * "Enviar ao {produtor}" — abre o WhatsApp DELE, com o texto e o link `/p/`.
 *
 * ⚠️ A PLATAFORMA NÃO MANDA MENSAGEM. O servidor devolve texto e link; quem toca
 * em enviar é o corretor. O evento `LINK_ENVIADO` registra a INTENÇÃO, gravado
 * antes de abrir o WhatsApp — não há como saber se a mensagem saiu, e esperar
 * por uma confirmação que não existe deixaria o histórico vazio para sempre.
 */
function BotaoEnviar({ refRomaneio, primeiroNome }: { refRomaneio: string; primeiroNome: string }) {
  const { toast } = useToast();
  const [buscando, setBuscando] = useState(false);
  const mensagem = useQuery<{ texto: string; url: string; link: string | null; produtorNome: string | null }>({
    queryKey: ["/api/mercado/comprador/romaneios", "mensagem", refRomaneio],
    queryFn: () => fetchJson(`/api/mercado/comprador/romaneios/${encodeURIComponent(refRomaneio)}/mensagem`),
    enabled: buscando,
  });

  async function enviar() {
    if (!mensagem.data) { setBuscando(true); return; }
    try {
      await apiRequest("POST", `/api/mercado/comprador/romaneios/${encodeURIComponent(refRomaneio)}/link-enviado`);
    } catch {
      // Gravar o evento é HISTÓRICO; travar o envio porque ele não gravou seria
      // impedir o ato por causa do registro dele. O WhatsApp abre do mesmo jeito.
    }
    if (mensagem.data.link) window.open(mensagem.data.link, "_blank", "noopener");
    else {
      // Sem telefone não há `wa.me` — copiar o texto é o que resta, e dizer isso
      // é melhor do que um botão que não faz nada.
      await navigator.clipboard?.writeText(mensagem.data.texto).catch(() => undefined);
      toast({ title: "Texto copiado", description: "Este produtor não tem WhatsApp cadastrado." });
    }
  }

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={enviar}
      disabled={buscando && mensagem.isLoading}
      data-testid="button-enviar-produtor"
    >
      <MessageCircle className="h-4 w-4 mr-1.5" />
      {buscando && mensagem.isLoading ? "Preparando…" : `Enviar ao ${primeiroNome}`}
    </Button>
  );
}

function BotaoExcluir({ refRomaneio, onExcluido }: { refRomaneio: string; onExcluido: () => void }) {
  const { toast } = useToast();
  const [confirmando, setConfirmando] = useState(false);
  const excluir = useExcluirRomaneio();

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="text-destructive"
        onClick={() => setConfirmando(true)}
        data-testid="button-excluir-romaneio"
      >
        <Trash2 className="h-4 w-4 mr-1.5" />Excluir
      </Button>
      <Dialog open={confirmando} onOpenChange={o => !o && setConfirmando(false)}>
        <DialogContent className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>Excluir este romaneio?</DialogTitle>
            <DialogDescription>
              O registro do que você comprou some da lista e de "A pagar". Não há como desfazer.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmando(false)}>Cancelar</Button>
            <Button
              variant="destructive"
              disabled={excluir.isPending}
              onClick={() => excluir.mutate(refRomaneio, {
                onSuccess: () => { setConfirmando(false); onExcluido(); toast({ title: "Romaneio excluído" }); },
                onError: (e: Error) => toast({ title: "Não consegui excluir", description: e.message, variant: "destructive" }),
              })}
              data-testid="button-confirmar-excluir"
            >
              {excluir.isPending ? "Excluindo…" : "Excluir"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

/**
 * A rota `/mercado/romaneios/:ref` monta a LISTA com o painel por cima — ver
 * `romaneios.tsx`. Este default existe só para o roteador não precisar conhecer
 * a diferença; quem renderiza o painel é a lista.
 */
export default PainelRomaneio;
